import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import Resizer from "react-image-file-resizer";

const DAY_NAMES = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
const COUNTRY_COLUMN_ALIASES = [
  "CODIGO_PAIS",
  "CODIGO DEL PAIS",
  "CODIGO_DEL_PAIS",
  "PAIS_CODIGO",
  "COD_PAIS",
  "PAIS",
];
const PHONE_COLUMN_ALIASES = [
  "TELEFONO",
  "TELÉFONO",
  "TELEFONO_CONTACTO",
  "NUMERO",
  "NUMERO_TELEFONO",
  "NUMERO TELÉFONO",
  "CELULAR",
];
const DESDE_COLUMN_ALIASES = ["DESDE", "FECHA_DESDE", "INICIO", "VIGENCIA_DESDE"];
const HASTA_COLUMN_ALIASES = ["HASTA", "FECHA_HASTA", "FIN", "VIGENCIA_HASTA"];

const ID_IMAGE_RESIZE = {
  width: 1024,
  height: 640,
  quality: 85,
};

const TEMPLATE_PATH = "/plantillas/plantilla_frecc_proveedor.xlsx";

const resizeImageToBase64 = file =>
  new Promise((resolve, reject) => {
    try {
      Resizer.imageFileResizer(
        file,
        ID_IMAGE_RESIZE.width,
        ID_IMAGE_RESIZE.height,
        "JPEG",
        ID_IMAGE_RESIZE.quality,
        0,
        uri => resolve(uri),
        "base64",
      );
    } catch (error) {
      reject(error);
    }
  });

const readFileAsDataUrl = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("No se pudo leer el archivo"));
    reader.readAsDataURL(file);
  });

const convertFileToBase64 = async file => {
  try {
    return await resizeImageToBase64(file);
  } catch (error) {
    return readFileAsDataUrl(file);
  }
};

const ProveedoresModal = React.forwardRef(function ProveedoresModal(
  { viviendas, subfraccionamientos, subLabel, selectedSubfraccionamiento },
  ref,
) {
  const [subfraccionamiento, setSubfraccionamiento] = useState(selectedSubfraccionamiento || 0);
  const [idViv, setIdViv] = useState(0);
  const [empresa, setEmpresa] = useState("");
  const [excelName, setExcelName] = useState("");

  // Images Logic
  const photosInputRef = useRef(null);
  const objectUrlsRef = useRef(new Set());

  // Column keys and labels for the new INE photo group
  const INE_FRENTE_KEY = "INE_FRENTE";
  const INE_ATRAS_KEY = "INE_ATRAS";
  const PHOTO_GROUP_LABEL = "INE";
  const ALLOWED_IMAGE_EXTS = new Set(["jpg", "jpeg", "png", "webp", "gif"]);

  // Revoke previously created object URLs (avoid memory leaks)
  const revokeAllObjectUrls = useCallback(() => {
    try {
      objectUrlsRef.current.forEach(u => URL.revokeObjectURL(u));
      objectUrlsRef.current.clear();
    } catch {
      /* noop */
    }
  }, []);

  // columnas calculadas + filas
  const [columns, setColumns] = useState([]); // [{ key, label, kind }] - kind: 'base' | 'active' | 'start' | 'end'
  const [tableData, setTableData] = useState([]);
  const [accessStates, setAccessStates] = useState({});
  const [hoverIssue, setHoverIssue] = useState(null);

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (selectedSubfraccionamiento === 0 || selectedSubfraccionamiento) {
      setSubfraccionamiento(selectedSubfraccionamiento);
      if (selectedSubfraccionamiento === 0) setIdViv(0);
    }
  }, [selectedSubfraccionamiento]);

  useEffect(() => {
    if (!excelName && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [excelName]);

  const filtarViviendas = () => {
    if (!Array.isArray(viviendas) || viviendas.length === 0) return [];
    return viviendas.filter(viv => String(viv.id_subfraccionamiento) === String(subfraccionamiento));
  };

  const handleSubfraccionamiento = event => {
    const value = event.target.value;
    setSubfraccionamiento(value);
    setIdViv(0);
  };

  const handleOpenInstructivo = useCallback(() => {
    window.open("/instructivo/InstructivoProveedores.pdf", "_blank", "noopener,noreferrer");
  }, []);

  // ---------- Helpers ----------
  const normalize = s =>
    String(s ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // quita acentos
      .trim()
      .replace(/\s+/g, " ");

  const upperKey = s =>
    normalize(s)
      .toUpperCase()
      .replace(/[^\w]+/g, "_")
      .replace(/^_|_$/g, "");

  const slugForPhoto = s =>
    normalize(s)
      .toLowerCase()
      .replace(/\s+/g, "") // remove spaces
      .replace(/[^a-z0-9]/g, ""); // keep only a-z0-9

  const formatTime = v => {
    if (v == null || v === "") return "";
    // Si viene como Date (p.ej. 1899-12-31 08:30)
    if (v instanceof Date) {
      const hh = v.getHours().toString().padStart(2, "0");
      const mm = v.getMinutes().toString().padStart(2, "0");
      return `${hh}:${mm}`;
    }
    // Si viene como número Excel (fracción del día)
    if (typeof v === "number") {
      const totalMinutes = Math.round((v % 1) * 24 * 60);
      const hh = Math.floor(totalMinutes / 60)
        .toString()
        .padStart(2, "0");
      const mm = (totalMinutes % 60).toString().padStart(2, "0");
      return `${hh}:${mm}`;
    }
    // Si viene como texto (e.g. "8:30", "08:30", "08:30:00"), lo normalizamos
    const txt = String(v).trim();
    const m = txt.match(/^(\d{1,2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/);
    if (m) {
      const hh = m[1].padStart(2, "0");
      const mm = m[2];
      return `${hh}:${mm}`;
    }
    return txt;
  };

  const formatDate = v => {
    if (v == null || v === "") return "";

    const toOutput = (yyyy, mm, dd) => {
      if (
        typeof yyyy !== "number" ||
        Number.isNaN(yyyy) ||
        typeof mm !== "number" ||
        Number.isNaN(mm) ||
        typeof dd !== "number" ||
        Number.isNaN(dd)
      ) {
        return "";
      }
      return `${dd.toString().padStart(2, "0")}/${mm.toString().padStart(2, "0")}/${yyyy}`;
    };

    if (v instanceof Date && !Number.isNaN(v.getTime())) {
      return toOutput(v.getFullYear(), v.getMonth() + 1, v.getDate());
    }

    if (typeof v === "number" && Number.isFinite(v) && XLSX?.SSF?.parse_date_code) {
      const parsed = XLSX.SSF.parse_date_code(v);
      if (parsed) {
        return toOutput(parsed.y, parsed.m, parsed.d);
      }
    }

    const txt = String(v).trim();
    if (!txt) return "";

    const isoMatch = txt.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
    if (isoMatch) {
      const [, yyyy, mm, dd] = isoMatch;
      return `${dd}-${mm}-${yyyy}`;
    }

    const parsedDate = new Date(txt);
    if (!Number.isNaN(parsedDate.getTime())) {
      return toOutput(parsedDate.getFullYear(), parsedDate.getMonth() + 1, parsedDate.getDate());
    }

    return txt;
  };

  const isActiveTruthy = v => {
    if (v == null) return false;
    const s = String(v).trim().toLowerCase();
    return s === "1" || s === "true" || s === "si" || s === "sí" || s === "x" || s === "y" || s === "yes";
  };

  const findNameColumnKey = cols => {
    if (!Array.isArray(cols) || cols.length === 0) return null;

    const candidates = new Set([
      "NOMBRE",
      "NOMBRE_COMPLETO",
      "NOMBRES",
      "NOMBRE_Y_APELLIDO",
      "NOMBRE_APELLIDO",
      "NOMBREPERSONA",
      "NOMBRE_PROVEEDOR",
      "NOMBRE DEL PROVEEDOR",
    ]);

    // 1) direct key match
    for (const c of cols) {
      const key = String(c.key || "").toUpperCase();
      if (c.kind === "base" && candidates.has(key)) return c.key;
    }

    // 2) heuristics by label or key
    for (const c of cols) {
      const label = String(c.label || "").toUpperCase();
      const key = String(c.key || "").toUpperCase();
      if (c.kind === "base" && (label.includes("NOMBRE") || key.includes("NOMBRE"))) return c.key;
    }

    // 3) fallback: first base column
    const firstBase = cols.find(c => c.kind === "base");
    return firstBase ? firstBase.key : null;
  };

  const pad2 = value => String(value).padStart(2, "0");

  const findColumnKeyByAliases = (cols, aliases = []) => {
    if (!Array.isArray(cols) || cols.length === 0 || !Array.isArray(aliases)) return null;
    const aliasKeys = aliases.map(alias => upperKey(alias));

    const matchByKey = cols.find(col => aliasKeys.includes(upperKey(col.key || "")));
    if (matchByKey) return matchByKey.key;

    const matchByLabel = cols.find(col => aliasKeys.includes(upperKey(col.label || "")));
    return matchByLabel ? matchByLabel.key : null;
  };

  const parseDateCell = value => {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      const year = value.getFullYear();
      const month = value.getMonth() + 1;
      const day = value.getDate();
      return {
        iso: `${year}-${pad2(month)}-${pad2(day)}`,
        date: new Date(year, month - 1, day),
      };
    }

    const raw = String(value ?? "").trim();
    if (!raw) return null;

    const normalized = raw.replace(/\./g, "/").replace(/-/g, "/");
    const parts = normalized
      .split("/")
      .map(part => part.trim())
      .filter(Boolean);

    if (parts.length === 3) {
      let year = null;
      let month = null;
      let day = null;

      if (parts[0].length === 4) {
        year = Number(parts[0]);
        month = Number(parts[1]);
        day = Number(parts[2]);
      } else if (parts[2].length === 4 || parts[2].length === 2) {
        day = Number(parts[0]);
        month = Number(parts[1]);
        year = Number(parts[2]);
        if (year < 100) {
          year += year >= 70 ? 1900 : 2000;
        }
      }

      if (
        Number.isFinite(year) &&
        Number.isFinite(month) &&
        Number.isFinite(day) &&
        month >= 1 &&
        month <= 12 &&
        day >= 1 &&
        day <= 31
      ) {
        const iso = `${year}-${pad2(month)}-${pad2(day)}`;
        return {
          iso,
          date: new Date(year, month - 1, day),
        };
      }
    }

    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
      const year = parsed.getFullYear();
      const month = parsed.getMonth() + 1;
      const day = parsed.getDate();
      return {
        iso: `${year}-${pad2(month)}-${pad2(day)}`,
        date: new Date(year, month - 1, day),
      };
    }

    return null;
  };

  const normalizeDigits = value =>
    String(value ?? "")
      .replace(/[^\d]/g, "")
      .trim();

  const sanitizePhoneNumber = value => normalizeDigits(value);

  const extractCountryCallingCode = value => {
    if (value == null) return "";
    const text = String(value);
    const match = text.match(/\(([^)]+)\)/);
    if (match && match[1]) {
      const digits = normalizeDigits(match[1]);
      if (digits) return digits;
    }
    const fallback = normalizeDigits(text);
    return fallback || "";
  };

  const buildPhoneWithCountry = (countryValue, phoneValue) => {
    const phoneDigits = sanitizePhoneNumber(phoneValue);
    const countryDigits = extractCountryCallingCode(countryValue);
    if (!phoneDigits && !countryDigits) {
      return { full: "", missingPhone: true, missingCountry: true };
    }
    if (!phoneDigits) {
      return { full: "", missingPhone: true, missingCountry: !countryDigits };
    }
    if (!countryDigits) {
      return { full: "", missingPhone: false, missingCountry: true };
    }
    return {
      full: `+${countryDigits}${phoneDigits}`,
      missingPhone: false,
      missingCountry: false,
    };
  };

  const ensureTimeFormat = value => {
    if (value == null) return "";
    const txt = String(value).trim();
    if (!txt) return "";
    const match = txt.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return txt;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return txt;
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
  };

  const timeToMinutes = value => {
    if (value == null) return null;
    const txt = String(value).trim();
    if (!txt) return null;
    const match = txt.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
    return hours * 60 + minutes;
  };

  const columnKeys = useMemo(
    () => ({
      name: findNameColumnKey(columns),
      country: findColumnKeyByAliases(columns, COUNTRY_COLUMN_ALIASES),
      phone: findColumnKeyByAliases(columns, PHONE_COLUMN_ALIASES),
      desde: findColumnKeyByAliases(columns, DESDE_COLUMN_ALIASES),
      hasta: findColumnKeyByAliases(columns, HASTA_COLUMN_ALIASES),
    }),
    [columns],
  );

  const nameColumnKey = columnKeys.name;
  const countryColumnKey = columnKeys.country;
  const phoneColumnKey = columnKeys.phone;
  const desdeColumnKey = columnKeys.desde;
  const hastaColumnKey = columnKeys.hasta;

  // Construye columnas a partir de dos filas de encabezado
  const columnVisualWidth = kind => {
    switch (kind) {
      case "base":
        return 140;
      case "active":
        return 90;
      case "access":
        return 120;
      case "date":
        return 140;
      case "photo":
        return 140;
      case "start":
      case "end":
      default:
        return 110;
    }
  };

  const buildColumnsFromTwoHeaderRows = (row1, row2) => {
    const dayPretty = DAY_NAMES;
    const dayKeyToPretty = new Map();
    dayPretty.forEach(day => {
      dayKeyToPretty.set(upperKey(day), day);
    });
    const dayKeys = new Set(dayKeyToPretty.keys());

    const cols = [];
    let lastDayKey = null;

    for (let i = 0; i < Math.max(row1.length, row2.length); i++) {
      const parentRaw = row1[i] ?? "";
      const childRaw = row2[i] ?? "";

      const parentKey = upperKey(parentRaw);
      const childKey = upperKey(childRaw);

      if (dayKeys.has(parentKey)) {
        lastDayKey = parentKey;
      }

      const matchesActive = /^ACTIVO?$/.test(childKey);
      const matchesStart = /^(INICIO|START|ENTRADA|ENTRY)$/.test(childKey);
      const matchesEnd = /^(FIN|END|SALIDA|EXIT)$/.test(childKey);

      const effectiveDayKey = dayKeys.has(parentKey)
        ? parentKey
        : dayKeys.has(childKey)
        ? childKey
        : lastDayKey && (matchesActive || matchesStart || matchesEnd)
        ? lastDayKey
        : null;

      if (effectiveDayKey && (matchesActive || matchesStart || matchesEnd)) {
        const prettyDay = dayKeyToPretty.get(effectiveDayKey) || normalize(parentRaw) || parentRaw || childRaw;
        if (matchesActive) {
          cols.push({
            key: `${effectiveDayKey}_ACTIVO`,
            label: "Activo",
            kind: "active",
            sourceIndex: i,
            group: prettyDay,
          });
        } else if (matchesStart) {
          cols.push({
            key: `${effectiveDayKey}_INICIO`,
            label: "Entrada",
            kind: "start",
            sourceIndex: i,
            group: prettyDay,
          });
        } else if (matchesEnd) {
          cols.push({
            key: `${effectiveDayKey}_FIN`,
            label: "Salida",
            kind: "end",
            sourceIndex: i,
            group: prettyDay,
          });
        }
        continue;
      }

      const labelSource = String(parentRaw).trim() !== "" ? parentRaw : childRaw;
      if (String(labelSource).trim() === "") continue;

      const baseKey = parentKey || childKey || `COL_${i + 1}`;
      const cleanLabel = String(labelSource).trim();
      cols.push({
        key: baseKey,
        label: cleanLabel || normalize(labelSource),
        kind: baseKey === "DESDE" || baseKey === "HASTA" ? "date" : "base",
        sourceIndex: i,
        group: null,
      });
    }

    // Evita duplicados manteniendo el primero
    const seen = new Set();
    const unique = [];
    for (const c of cols) {
      if (seen.has(c.key)) continue;
      seen.add(c.key);
      unique.push(c);
    }
    return unique;
  };

  // Lee Excel y mapea a columnas/filas usando 2 encabezados
  const handleFileChange = async event => {
    const files = event.target.files;
    if (!files || !files[0]) {
      setExcelName("");
      setColumns([]);
      setTableData([]);
      return;
    }
    const file = files[0];
    setExcelName(file.name);

    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: "array", cellDates: true, cellNF: false, cellText: false });
      const ws = wb.Sheets[wb.SheetNames[0]];
      if (!ws) {
        setColumns([]);
        setTableData([]);
        return;
      }

      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", blankrows: false });
      if (!rows || rows.length < 3) {
        // necesitamos al menos 2 filas de encabezado + 1 de datos
        setColumns([]);
        setTableData([]);
        return;
      }

      const headerRow1 = rows[0]; // días o bases
      const headerRow2 = rows[1]; // Activo/Inicio/Fin
      const cols = buildColumnsFromTwoHeaderRows(headerRow1, headerRow2);

      // cuerpo a partir de la fila 3
      const dataRows = rows.slice(2);
      const parsed = dataRows.map(r => {
        const obj = {};
        cols.forEach(c => {
          let v = r[c.sourceIndex];
          if (c.kind === "start" || c.kind === "end") {
            v = formatTime(v);
          } else if (c.kind === "active") {
            v = isActiveTruthy(v);
          } else if (c.kind === "date") {
            v = formatDate(v);
          } else {
            // base: queda tal cual
          }
          obj[c.key] = v ?? "";
        });
        return obj;
      });

      revokeAllObjectUrls();
      const parsedWithPhotosReset = parsed.map(r => ({
        ...r,
        [INE_FRENTE_KEY]: "",
        [INE_ATRAS_KEY]: "",
      }));
      setColumns(cols);
      setTableData(parsedWithPhotosReset);
    } catch (err) {
      console.error("XLSX parse error:", err);
      setColumns([]);
      setTableData([]);
    }
  };

  const handleDownloadTemplate = useCallback(() => {
    try {
      const link = document.createElement("a");
      link.href = TEMPLATE_PATH;
      link.download = "plantilla_frecc_proveedor.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      window.open(TEMPLATE_PATH, "_blank", "noopener,noreferrer");
    }
  }, []);

  const handlePhotosFolderChange = async event => {
    const files = Array.from(event?.target?.files || []);
    if (files.length === 0) return;

    // Build: slug -> { frente?: File, atras?: File }
    const filesBySlug = new Map();
    for (const f of files) {
      const name = String(f.name || "")
        .toLowerCase()
        .trim();
      const m = name.match(/^(.+?)_(frente|atr(?:a|á)s)\.(jpg|jpeg|png|webp|gif)$/i);
      if (!m) continue;

      let base = m[1] || "";
      let side = m[2] || "";
      const ext = (m[3] || "").toLowerCase();
      if (!ALLOWED_IMAGE_EXTS.has(ext)) continue;

      // normalize "atrás" vs "atras"
      side = side.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      if (side === "atr") side = "atras";

      const slug = slugForPhoto(base);
      if (!slug) continue;

      if (!filesBySlug.has(slug)) filesBySlug.set(slug, {});
      const bucket = filesBySlug.get(slug);
      if (side === "frente") bucket.frente = f;
      else if (side === "atras") bucket.atras = f;
    }

    const nameKey = nameColumnKey;
    if (!nameKey) {
      // cannot map → mark error in both photo cells
      setTableData(prev =>
        prev.map(r => ({
          ...r,
          [INE_FRENTE_KEY]: { status: "error" },
          [INE_ATRAS_KEY]: { status: "error" },
        })),
      );
      return;
    }

    revokeAllObjectUrls();

    const createPhotoEntry = async file => {
      try {
        const data = await convertFileToBase64(file);
        let url = null;
        try {
          url = URL.createObjectURL(file);
          if (url && url.startsWith("blob:")) {
            objectUrlsRef.current.add(url);
          }
        } catch {
          url = data;
        }
        return { status: "ok", url, data };
      } catch (error) {
        console.error("No se pudo procesar la foto de proveedor:", error);
        return { status: "error" };
      }
    };

    const processedBySlug = new Map();
    await Promise.all(
      Array.from(filesBySlug.entries()).map(async ([slug, bucket]) => {
        const entry = {};
        if (bucket.frente) {
          entry.frente = await createPhotoEntry(bucket.frente);
        }
        if (bucket.atras) {
          entry.atras = await createPhotoEntry(bucket.atras);
        }
        processedBySlug.set(slug, entry);
      }),
    );

    setTableData(prev =>
      prev.map(r => {
        const value = r?.[nameKey];
        const slug = slugForPhoto(value || "");
        const pair = slug ? processedBySlug.get(slug) : null;

        return {
          ...r,
          [INE_FRENTE_KEY]: pair?.frente ? pair.frente : { status: "missing" },
          [INE_ATRAS_KEY]: pair?.atras ? pair.atras : { status: "missing" },
        };
      }),
    );

    if (event?.target) {
      event.target.value = "";
    }
  };

  let viviendasFiltradas = [];
  if (subfraccionamiento !== 0) {
    viviendasFiltradas = filtarViviendas();
  }

  const getRowKey = (row, idx, cols) =>
    row.ID || row.Id || row.id || `${idx}:${cols.map(c => String(row[c.key] ?? "")).join("|")}`;

  const ACCESS_COLUMN_KEY = "__ACCESO__";
  const ACCESS_PRIMARY_COLOR = "#6d2e87";

  const dayConfigs = useMemo(
    () =>
      DAY_NAMES.map(dayName => ({
        day: dayName,
        key: upperKey(dayName),
      })),
    [],
  );

  const rowKeys = useMemo(() => tableData.map((row, idx) => getRowKey(row, idx, columns)), [tableData, columns]);

  useEffect(() => {
    setAccessStates(prev => {
      const next = {};
      let changed = false;

      rowKeys.forEach(key => {
        const prevValue = Object.prototype.hasOwnProperty.call(prev, key) ? prev[key] : false;
        next[key] = prevValue;
        if (!changed && prev[key] !== prevValue) {
          changed = true;
        }
      });

      if (!changed) {
        const prevKeys = Object.keys(prev);
        if (prevKeys.length !== rowKeys.length) {
          changed = true;
        } else if (prevKeys.some(key => !Object.prototype.hasOwnProperty.call(next, key))) {
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [rowKeys]);

  useEffect(() => {
    return () => {
      revokeAllObjectUrls();
    };
  }, []);

  useEffect(() => {
    setHoverIssue(null);
  }, [tableData]);

  const allAccessEnabled = rowKeys.length > 0 && rowKeys.every(key => !!accessStates[key]);

  const handleToggleRowAccess = key => {
    setAccessStates(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleToggleAllAccess = () => {
    if (rowKeys.length === 0) return;
    const shouldEnable = !allAccessEnabled;
    setAccessStates(prev => {
      const next = { ...prev };
      rowKeys.forEach(key => {
        next[key] = shouldEnable;
      });
      return next;
    });
  };

  const toggleAllLabel = allAccessEnabled ? "Marcar pendientes" : "Confirmar todos";
  const toggleAllButtonClass = `btn btn-mini ${allAccessEnabled ? "btn-primary" : "btn-secondary"} color-white`;

  const subLabelText = subLabel || "Subfraccionamiento";

  const computeValidationSummary = useCallback(
    ({ strict = false } = {}) => {
      const errors = [];
      const warnings = [];
      const cellIssues = tableData.map(() => ({}));
      const registerCellIssue = (rowIndex, columnKey, message, type = "error") => {
        if (rowIndex == null || rowIndex < 0) return;
        if (!columnKey || !cellIssues[rowIndex]) return;
        const bucket = cellIssues[rowIndex];
        if (!bucket[columnKey]) {
          bucket[columnKey] = { type, messages: [] };
        }
        bucket[columnKey].messages.push(message);
      };

      const viviendaId = Number(idViv) || 0;
      const empresaValue = empresa.trim();
      const subId = Number(subfraccionamiento) || 0;

      if (!excelName || tableData.length === 0) {
        if (strict) {
          errors.push("Debes importar un archivo con proveedores.");
        }
        return {
          errors,
          warnings,
          rows: [],
          meta: { viviendaId, empresa: empresaValue, subfraccionamiento: subId },
          cellIssues,
        };
      }

      if (viviendaId === 0) {
        errors.push("Debes seleccionar una vivienda.");
      }

      if (!empresaValue) {
        errors.push("Debes ingresar la empresa.");
      }

      if (subId === 0) {
        errors.push(`Debes seleccionar un ${subLabelText.toLowerCase()}.`);
      }

      const {
        name: nameColumnKey,
        country: countryColumnKey,
        phone: phoneColumnKey,
        desde: desdeColumnKey,
        hasta: hastaColumnKey,
      } = columnKeys;

      if (!nameColumnKey) errors.push('No se encontró la columna de "Nombre" en el archivo.');
      if (!countryColumnKey) errors.push('No se encontró la columna de "Código país" en el archivo.');
      if (!phoneColumnKey) errors.push('No se encontró la columna de "Teléfono" en el archivo.');
      if (!desdeColumnKey) errors.push('No se encontró la columna de "Desde" en el archivo.');
      if (!hastaColumnKey) errors.push('No se encontró la columna de "Hasta" en el archivo.');

      if (!nameColumnKey || !countryColumnKey || !phoneColumnKey || !desdeColumnKey || !hastaColumnKey) {
        return {
          errors,
          warnings,
          rows: [],
          meta: { viviendaId, empresa: empresaValue, subfraccionamiento: subId },
          cellIssues,
        };
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const maxDate = new Date(today);
      maxDate.setFullYear(maxDate.getFullYear() + 1);

      const nameTracker = new Map();
      const phoneTracker = new Map();
      const rowPayloads = Array(tableData.length).fill(null);

      tableData.forEach((row, rowIndex) => {
        const rowNumber = rowIndex + 1;
        const rawName = String(row?.[nameColumnKey] ?? "")
          .trim()
          .replace(/\s+/g, " ");
        const comparableName = upperKey(rawName || `__ROW_${rowNumber}`);
        const rowLabel = rawName ? `Fila ${rowNumber} (${rawName})` : `Fila ${rowNumber}`;
        let rowHasErrors = false;

        if (!rawName) {
          errors.push(`${rowLabel}: El nombre es obligatorio.`);
          registerCellIssue(rowIndex, nameColumnKey, "El nombre es obligatorio.");
          rowHasErrors = true;
        } else {
          if (!nameTracker.has(comparableName)) {
            nameTracker.set(comparableName, { label: rawName, rows: [rowNumber] });
          } else {
            nameTracker.get(comparableName).rows.push(rowNumber);
          }
        }

        const countryValue = row?.[countryColumnKey];
        const phoneValue = row?.[phoneColumnKey];
        const phoneBuild = buildPhoneWithCountry(countryValue, phoneValue);
        if (phoneBuild.missingCountry) {
          errors.push(`${rowLabel}: Debes indicar el código de país (ej. México (+52)).`);
          registerCellIssue(rowIndex, phoneColumnKey, "Agrega el código entre paréntesis en el país.");
          rowHasErrors = true;
        }
        if (phoneBuild.missingPhone) {
          errors.push(`${rowLabel}: El teléfono es obligatorio.`);
          registerCellIssue(rowIndex, phoneColumnKey, "Ingresa el teléfono.");
          rowHasErrors = true;
        }
        const fullPhone = phoneBuild.full;
        if (fullPhone) {
          if (!phoneTracker.has(fullPhone)) {
            phoneTracker.set(fullPhone, { phone: fullPhone, rows: [rowNumber] });
          } else {
            phoneTracker.get(fullPhone).rows.push(rowNumber);
          }
        }

        const desdeParsed = parseDateCell(row?.[desdeColumnKey]);
        const hastaParsed = parseDateCell(row?.[hastaColumnKey]);

        if (!desdeParsed) {
          errors.push(`${rowLabel}: La fecha "Desde" es obligatoria y debe tener un formato válido.`);
          registerCellIssue(rowIndex, desdeColumnKey, 'Revisa la fecha "Desde".');
          rowHasErrors = true;
        } else if (desdeParsed.date < today) {
          errors.push(`${rowLabel}: La fecha "Desde" no puede ser anterior a hoy.`);
          registerCellIssue(rowIndex, desdeColumnKey, 'No puede ser anterior a hoy.');
          rowHasErrors = true;
        } else if (desdeParsed.date > maxDate) {
          errors.push(`${rowLabel}: La fecha "Desde" no puede ser mayor a un año desde hoy.`);
          registerCellIssue(rowIndex, desdeColumnKey, "Máximo un año a futuro.");
          rowHasErrors = true;
        }

        if (!hastaParsed) {
          errors.push(`${rowLabel}: La fecha "Hasta" es obligatoria y debe tener un formato válido.`);
          registerCellIssue(rowIndex, hastaColumnKey, 'Revisa la fecha "Hasta".');
          rowHasErrors = true;
        } else if (hastaParsed.date < today) {
          errors.push(`${rowLabel}: La fecha "Hasta" no puede ser anterior a hoy.`);
          registerCellIssue(rowIndex, hastaColumnKey, 'No puede ser anterior a hoy.');
          rowHasErrors = true;
        } else if (hastaParsed.date > maxDate) {
          errors.push(`${rowLabel}: La fecha "Hasta" no puede ser mayor a un año desde hoy.`);
          registerCellIssue(rowIndex, hastaColumnKey, "Máximo un año a futuro.");
          rowHasErrors = true;
        }

        if (desdeParsed && hastaParsed && hastaParsed.date < desdeParsed.date) {
          errors.push(`${rowLabel}: La fecha "Hasta" no puede ser anterior a la fecha "Desde".`);
          registerCellIssue(rowIndex, desdeColumnKey, '"Desde" debe ser menor.');
          registerCellIssue(rowIndex, hastaColumnKey, '"Hasta" debe ser mayor.');
          rowHasErrors = true;
        }

        const horarios = dayConfigs.map(config => {
          const keyPrefix = config.key;
          const active = Boolean(row?.[`${keyPrefix}_ACTIVO`]);
          const startRaw = ensureTimeFormat(row?.[`${keyPrefix}_INICIO`] || "");
          const endRaw = ensureTimeFormat(row?.[`${keyPrefix}_FIN`] || "");
          const hasAnyTime = Boolean(startRaw) || Boolean(endRaw);

          const startMinutes = startRaw ? timeToMinutes(startRaw) : null;
          const endMinutes = endRaw ? timeToMinutes(endRaw) : null;

          if ((active || hasAnyTime) && (startMinutes == null || endMinutes == null)) {
            errors.push(`${rowLabel}: El horario de ${config.day} es inválido.`);
            registerCellIssue(rowIndex, `${keyPrefix}_INICIO`, "Hora inválida.");
            registerCellIssue(rowIndex, `${keyPrefix}_FIN`, "Hora inválida.");
            rowHasErrors = true;
          } else if (startMinutes != null && endMinutes != null && startMinutes >= endMinutes) {
            errors.push(
              `${rowLabel}: El horario de ${config.day} debe tener una hora de salida mayor que la de entrada.`,
            );
            registerCellIssue(rowIndex, `${keyPrefix}_INICIO`, "La entrada debe ser menor.");
            registerCellIssue(rowIndex, `${keyPrefix}_FIN`, "La salida debe ser mayor.");
            rowHasErrors = true;
          }

          return {
            day: config.day,
            active: Boolean(active),
            start: startRaw || "00:00",
            end: endRaw || "00:00",
          };
        });

        const rowKey = rowKeys[rowIndex];
        const isConfirmed = rowKey ? Boolean(accessStates[rowKey]) : false;
        const frenteValue = row?.[INE_FRENTE_KEY];
        const atrasValue = row?.[INE_ATRAS_KEY];
        const hasFrente = Boolean(
          frenteValue && typeof frenteValue === "object" && frenteValue.status === "ok" && frenteValue.data,
        );
        const hasAtras = Boolean(
          atrasValue && typeof atrasValue === "object" && atrasValue.status === "ok" && atrasValue.data,
        );

        let acceso = isConfirmed ? "CONFIRMADO" : "PENDIENTE";
        if (acceso === "CONFIRMADO" && (!hasFrente || !hasAtras)) {
          acceso = "PENDIENTE";
          warnings.push(
            `${rowLabel}: Confirmaste el acceso pero no agregaste ambas fotos, se enviará como PENDIENTE.`,
          );
          if (!hasFrente) {
            registerCellIssue(rowIndex, INE_FRENTE_KEY, "Agrega la foto frontal para confirmar.", "warning");
          }
          if (!hasAtras) {
            registerCellIssue(rowIndex, INE_ATRAS_KEY, "Agrega la foto trasera para confirmar.", "warning");
          }
        }

        if (!rowHasErrors && desdeParsed && hastaParsed && fullPhone && rawName) {
          rowPayloads[rowIndex] = {
            nombre: rawName,
            telefono: fullPhone,
            acceso,
            horarios,
            fecha: desdeParsed.iso,
            fecha2: hastaParsed.iso,
            empresa: empresaValue,
            vivienda: viviendaId,
            ine1: hasFrente ? frenteValue.data : null,
            ine2: hasAtras ? atrasValue.data : null,
            rowNumber,
            rowLabel,
          };
        } else {
          rowPayloads[rowIndex] = null;
        }
      });

      nameTracker.forEach(info => {
        if (info.rows.length > 1) {
          errors.push(`El nombre "${info.label}" está duplicado en las filas ${info.rows.join(", ")}.`);
          info.rows.forEach(rowNumber => {
            const idx = rowNumber - 1;
            if (idx >= 0 && idx < rowPayloads.length) {
              rowPayloads[idx] = null;
              registerCellIssue(idx, nameColumnKey, "Nombre duplicado en el archivo.", "error");
            }
          });
        }
      });

      phoneTracker.forEach(info => {
        if (info.rows.length > 1) {
          errors.push(`El teléfono ${info.phone} está duplicado en las filas ${info.rows.join(", ")}.`);
          info.rows.forEach(rowNumber => {
            const idx = rowNumber - 1;
            if (idx >= 0 && idx < rowPayloads.length) {
              rowPayloads[idx] = null;
              registerCellIssue(idx, phoneColumnKey, "Teléfono duplicado en el archivo.", "error");
            }
          });
        }
      });

      const validRows = rowPayloads.filter(Boolean);

      return {
        errors,
        warnings,
        rows: validRows,
        meta: { viviendaId, empresa: empresaValue, subfraccionamiento: subId },
        cellIssues,
      };
    },
    [accessStates, columnKeys, dayConfigs, empresa, excelName, idViv, rowKeys, subLabelText, subfraccionamiento, tableData],
  );

  const validationSummary = useMemo(() => computeValidationSummary({ strict: false }), [computeValidationSummary]);

  const resetFormState = useCallback(() => {
    revokeAllObjectUrls();
    const initialSub = selectedSubfraccionamiento === 0 || selectedSubfraccionamiento ? selectedSubfraccionamiento : 0;
    setSubfraccionamiento(initialSub);
    setIdViv(0);
    setEmpresa("");
    setExcelName("");
    setColumns([]);
    setTableData([]);
    setAccessStates({});
    setHoverIssue(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (photosInputRef.current) {
      photosInputRef.current.value = "";
    }
  }, [revokeAllObjectUrls, selectedSubfraccionamiento]);

  useImperativeHandle(
    ref,
    () => ({
      collectBulkPayload: () => computeValidationSummary({ strict: true }),
      resetForm: resetFormState,
    }),
    [computeValidationSummary, resetFormState],
  );
  const handleIssueMouseEnter = useCallback((event, issue) => {
    if (!issue || !Array.isArray(issue.messages) || issue.messages.length === 0) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    setHoverIssue({
      message: issue.messages.join(" · "),
      type: issue.type,
      x: rect.left + rect.width / 3,
      y: rect.top,
    });
  }, []);

  const handleIssueMouseLeave = useCallback(() => {
    setHoverIssue(null);
  }, []);

  const ISSUES_PREVIEW_LIMIT = 5;
  const errorPreview = validationSummary.errors.slice(0, ISSUES_PREVIEW_LIMIT);
  const warningPreview = validationSummary.warnings.slice(0, ISSUES_PREVIEW_LIMIT);
  const extraErrors = validationSummary.errors.length - errorPreview.length;
  const extraWarnings = validationSummary.warnings.length - warningPreview.length;

  const displayColumns = useMemo(() => {
    const filtered = columns.filter(col => col.key !== countryColumnKey);
    const base = [...filtered];

    if (excelName) {
      base.push(
        { key: INE_FRENTE_KEY, label: "Frente", kind: "photo", group: PHOTO_GROUP_LABEL },
        { key: INE_ATRAS_KEY, label: "Atrás", kind: "photo", group: PHOTO_GROUP_LABEL },
      );
    }

    const accessColumn = {
      key: ACCESS_COLUMN_KEY,
      label: "Acceso",
      kind: "access",
      group: null,
    };

    const nameIndex = nameColumnKey ? base.findIndex(col => col.key === nameColumnKey) : -1;
    if (nameIndex >= 0) {
      base.splice(nameIndex + 1, 0, accessColumn);
    } else {
      base.unshift(accessColumn);
    }

    return base;
  }, [columns, countryColumnKey, excelName, nameColumnKey]);

  const stickyKeys = useMemo(() => {
    const keys = [];
    if (nameColumnKey) keys.push(nameColumnKey);
    keys.push(ACCESS_COLUMN_KEY);
    return keys.filter((key, index, arr) => key && arr.indexOf(key) === index);
  }, [nameColumnKey]);

  const stickyOffsets = useMemo(() => {
    let offset = 0;
    const offsets = new Map();
    const stickySet = new Set(stickyKeys);
    displayColumns.forEach(col => {
      if (stickySet.has(col.key)) {
        offsets.set(col.key, offset);
        offset += columnVisualWidth(col.kind);
      }
    });
    return offsets;
  }, [displayColumns, stickyKeys]);

  const estimatedTableWidth = displayColumns.reduce((sum, col) => sum + columnVisualWidth(col.kind), 0);
  const MIN_TABLE_WIDTH = 600;

  const firstHeaderCells = [];
  displayColumns.forEach((col, idx) => {
    if (col.key === ACCESS_COLUMN_KEY) {
      firstHeaderCells.push({
        key: col.key,
        label: col.label,
        colSpan: 1,
        rowSpan: 2,
        isGroup: false,
        startIndex: idx,
        endIndex: idx,
        isAccess: true,
      });
      return;
    }
    const groupName = col.group ?? null;
    if (groupName) {
      const lastCell = firstHeaderCells[firstHeaderCells.length - 1];
      if (lastCell && lastCell.isGroup && lastCell.label === groupName) {
        lastCell.colSpan += 1;
        lastCell.endIndex = idx;
      } else {
        firstHeaderCells.push({
          key: `${groupName}:${idx}`,
          label: groupName,
          colSpan: 1,
          rowSpan: 1,
          isGroup: true,
          startIndex: idx,
          endIndex: idx,
        });
      }
    } else {
      firstHeaderCells.push({
        key: col.key,
        label: col.label,
        colSpan: 1,
        rowSpan: 2,
        isGroup: false,
        startIndex: idx,
        endIndex: idx,
      });
    }
  });

  const groupedColumns = displayColumns
    .map((col, idx) => {
      if (col.key === ACCESS_COLUMN_KEY || !col.group) return null;
      const groupName = col.group ?? null;
      if (!groupName) return null;
      return {
        column: col,
        columnIndex: idx,
      };
    })
    .filter(item => item !== null);

  return (
    <div className="full row align-start" style={{ width: "100%", gap: 16, minWidth: 0 }}>
      <div className="column form-column" style={{ width: "100%", minWidth: 0 }}>
        {/* Subfraccionamiento */}
        <div className="input-form-content row full align-center">
          <div className="column label-medium">
            <p>
              <b>{subLabelText}:</b>
            </p>
          </div>
          <div className="column full">
            <select
              className="input input-modals"
              name="subfraccionamiento"
              value={subfraccionamiento}
              onChange={handleSubfraccionamiento}
              disabled={subfraccionamiento == 0 ? false : true}
            >
              <option value="0">SELECCIONAR {subLabelText.toUpperCase()}</option>
              {Array.isArray(subfraccionamientos) &&
                subfraccionamientos.map(viv => (
                  <option key={viv.id_subfraccionamiento} value={viv.id_subfraccionamiento}>
                    {viv.nombre}
                  </option>
                ))}
            </select>
          </div>
        </div>

        <div className="white-space-8" />

        {/* Vivienda */}
        <div className="input-form-content row full align-center">
          <div className="column label-medium">
            <p>
              <b>Vivienda:</b>
            </p>
          </div>
          <div className="column full">
            <select
              className="input input-modals"
              name="vivienda"
              value={idViv}
              onChange={event => setIdViv(Number(event.target.value))}
              id="viv"
            >
              <option value="0">SELECCIONAR VIVIENDA</option>
              {viviendasFiltradas.map(viv => (
                <option key={viv.id_vivienda} value={viv.id_vivienda}>
                  {viv.numero_registro}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="white-space-8" />

        {/* Empresa */}
        <div className="input-form-content row full align-center">
          <div className="column label-medium">
            <p>
              <b>Empresa:</b>
            </p>
          </div>
          <div className="column full">
            <input
              type="text"
              name="company"
              value={empresa}
              onChange={event => setEmpresa(event.target.value)}
              className="input input-modals"
              autoComplete="off"
              maxLength={64}
            />
          </div>
        </div>

        <div className="white-space-8" />

        {/* Archivo (Excel) */}
        <div className="input-form-content row full align-center">
          <div className="column label-medium">
            <p>
              <b>Archivo (Excel):</b>
            </p>
          </div>
          <div className="column full">
            <div className="row align-center">
              <div className="column full">
                <input
                  type="file"
                  className="input input-modals input-file"
                  ref={fileInputRef}
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                />
                <input
                  type="text"
                  disabled
                  className="input input-modals"
                  value={excelName || ""}
                  placeholder="Selecciona un archivo"
                />
              </div>
              <div style={{ width: 8 }} />
              <div className="column">
                <button
                  type="button"
                  className="btn btn-primary btn-small color-white label-input-file"
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                >
                  Importar
                </button>
              </div>
              <div style={{ width: 8 }} />
              <div className="column">
                <button
                  type="button"
                  className="btn btn-primary btn-small color-white"
                  onClick={handleDownloadTemplate}
                >
                  Plantilla
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="white-space-16" />

        {/* Tabla */}
        {excelName ? (
          <div className="column" style={{ width: "100%" }}>
            <div className="row align-center" style={{ justifyContent: "space-between", gap: 8 }}>
              <p style={{ margin: 0 }}>
                <b>Registros detectados del archivo:</b>
              </p>
              <div className="row" style={{ gap: 8 }}>
                <button
                  type="button"
                  className="btn btn-secondary btn-small color-white"
                  onClick={handleOpenInstructivo}
                  title="Ver instructivo"
                >
                  <i className="fas fa-info-circle" aria-hidden="true" />
                </button>
                {/* Hidden input that can accept a whole folder of images */}
                <input
                  ref={photosInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: "none" }}
                  onChange={handlePhotosFolderChange}
                  // Non-standard but widely supported to pick directories in Chromium-based browsers
                  webkitdirectory="true"
                  directory="true"
                />
                <button
                  type="button"
                  className="btn btn-secondary btn-small color-white"
                  onClick={() => photosInputRef.current && photosInputRef.current.click()}
                  title="Selecciona una carpeta o varias fotos"
                >
                  Agregar fotos
                </button>
              </div>
            </div>
            <div className="white-space-8" />
            <div style={{ width: "100%", maxWidth: "100%", overflow: "hidden", minWidth: 0 }}>
              <div
                style={{
                  maxHeight: 320,
                  overflowX: "auto",
                  overflowY: "auto",
                  width: "100%",
                  minWidth: 0,
                }}
              >
                <table
                  className="table-history full"
                  style={{
                    tableLayout: "fixed",
                    width: "100%",
                    borderCollapse: "separate",
                    borderSpacing: 0,
                  }}
                >
                  {/* estira un poco las columnas */}
                  <colgroup>
                    {displayColumns.map(col => (
                      <col
                        key={col.key}
                        style={{
                          width: `${columnVisualWidth(col.kind)}px`,
                        }}
                      />
                    ))}
                  </colgroup>
                  <thead>
                    {columns.length === 0 ? (
                      <tr>
                        <th className="th-sticky text-right border-left">
                          <span style={{ color: "#666" }}>NOMBRE</span>
                        </th>
                        <th className="th-sticky text-center">
                          <span style={{ color: "#666" }}>TELÉFONO</span>
                        </th>
                        <th className="th-sticky text-center">
                          <span style={{ color: "#666" }}>LUNES (Activo)</span>
                        </th>
                        <th className="th-sticky text-center">
                          <span style={{ color: "#666" }}>LUNES Inicio</span>
                        </th>
                        <th className="th-sticky text-center">
                          <span style={{ color: "#666" }}>LUNES Fin</span>
                        </th>
                        <th className="th-sticky text-center border-right">
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 6,
                            }}
                          >
                            <span style={{ color: "#666" }}>Acceso</span>
                            <button
                              type="button"
                              className={toggleAllButtonClass}
                              onClick={handleToggleAllAccess}
                              disabled={rowKeys.length === 0}
                              title={toggleAllLabel}
                            >
                              {toggleAllLabel}
                            </button>
                          </div>
                        </th>
                      </tr>
                    ) : (
                      <>
                        <tr>
                          {firstHeaderCells.map(cell => {
                            const isFirst = cell.startIndex === 0;
                            const isLast = cell.endIndex === displayColumns.length - 1;
                            const className = [
                              "th-sticky",
                              "text-center",
                              isFirst ? "border-left" : "",
                              isLast ? "border-right" : "",
                            ]
                              .filter(Boolean)
                              .join(" ");
                            const cellStickyOffset = !cell.isGroup ? stickyOffsets.get(cell.key) : undefined;
                            const isSticky = cellStickyOffset != null;
                            return (
                              <th
                                key={cell.key}
                                className={className}
                                colSpan={cell.colSpan}
                                rowSpan={cell.rowSpan}
                                title={cell.isAccess ? undefined : cell.label}
                                style={{
                                  padding: "4px 2px",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  top: 0,
                                  position: isSticky ? "sticky" : undefined,
                                  left: isSticky ? cellStickyOffset : undefined,
                                  zIndex: isSticky ? 6 : 3,
                                }}
                              >
                                {cell.isAccess ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: 3,
                                    }}
                                  >
                                    <span style={{ color: "#666" }}>{cell.label}</span>
                                    <button
                                      type="button"
                                      className={toggleAllButtonClass}
                                      onClick={handleToggleAllAccess}
                                      disabled={rowKeys.length === 0}
                                      title={toggleAllLabel}
                                    >
                                      {toggleAllLabel}
                                    </button>
                                  </div>
                                ) : (
                                  <span style={{ color: "#666" }}>{cell.label}</span>
                                )}
                              </th>
                            );
                          })}
                        </tr>
                        {groupedColumns.length > 0 && (
                          <tr>
                            {groupedColumns.map(({ column, columnIndex }) => {
                              const firstGroupedIndex = groupedColumns[0]?.columnIndex ?? columnIndex;
                              const lastGroupedIndex =
                                groupedColumns[groupedColumns.length - 1]?.columnIndex ?? columnIndex;
                              const isFirst = columnIndex === firstGroupedIndex;
                              const isLast = columnIndex === lastGroupedIndex;
                              const className = [
                                "th-sticky",
                                "text-center",
                                isFirst ? "border-left" : "",
                                isLast ? "border-right" : "",
                              ]
                                .filter(Boolean)
                                .join(" ");
                              const stickyOffset = stickyOffsets.get(column.key);
                              const isSticky = stickyOffset != null;
                              return (
                                <th
                                  key={`${column.key}-sub`}
                                  className={className}
                                  title={`${column.group} - ${column.label}`}
                                  style={{
                                    padding: "4px 2px",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    top: 35,
                                    position: isSticky ? "sticky" : undefined,
                                    left: isSticky ? stickyOffset : undefined,
                                    background: isSticky ? "#fff" : undefined,
                                    zIndex: isSticky ? 5 : 2,
                                  }}
                                >
                                  <span style={{ color: "#666" }}>{column.label}</span>
                                </th>
                              );
                            })}
                          </tr>
                        )}
                      </>
                    )}
                  </thead>
                  <tbody>
                    {tableData.length === 0 ? (
                      <tr>
                        <td colSpan={Math.max(displayColumns.length, 5)} className="text-center">
                          No hay datos que mostrar
                        </td>
                      </tr>
                    ) : (
                      tableData.map((row, rIdx) => {
                        const rowKey = rowKeys[rIdx] ?? getRowKey(row, rIdx, columns);
                        const isRowConfirmed = !!accessStates[rowKey];
                        const accessLabel = isRowConfirmed ? "Confirmado" : "Pendiente";
                        const accessButtonStyles = {
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 20,
                          height: 20,
                          borderRadius: 2,
                          border: `1px solid ${ACCESS_PRIMARY_COLOR}`,
                          backgroundColor: isRowConfirmed ? ACCESS_PRIMARY_COLOR : "transparent",
                          color: isRowConfirmed ? "#fff" : ACCESS_PRIMARY_COLOR,
                          fontSize: "0.85rem",
                          transition: "all 0.2s ease-in-out",
                        };

                        return (
                          <tr key={rowKey}>
                            {displayColumns.map((col, cIdx) => {
                              const isFirst = cIdx === 0;
                              const isLast = cIdx === displayColumns.length - 1;
                              const commonClasses = [
                                isFirst ? "text-left" : "text-center",
                                isFirst ? "border-left" : "",
                                isLast ? "border-right" : "",
                              ]
                                .filter(Boolean)
                                .join(" ");
                              const baseCellStyle = {
                                padding: "8px 6px",
                                wordWrap: "break-word",
                              };
                              const issue = validationSummary.cellIssues?.[rIdx]?.[col.key];
                              const hasIssue = issue && Array.isArray(issue.messages) && issue.messages.length > 0;
                              const issueBorderColor = issue?.type === "warning" ? "#e0a500" : "#d14343";
                              const issueBgColor = issue?.type === "warning" ? "#fff8e5" : "#fdecea";
                              if (hasIssue) {
                                baseCellStyle.background = issueBgColor;
                                baseCellStyle.boxShadow = `inset 0 0 0 2px ${issueBorderColor}`;
                                baseCellStyle.cursor = "help";
                              }
                              const issueHandlers = hasIssue
                                ? {
                                    onMouseEnter: event => handleIssueMouseEnter(event, issue),
                                    onMouseLeave: handleIssueMouseLeave,
                                  }
                                : {};
                              const stickyOffset = stickyOffsets.get(col.key);
                              if (stickyOffset != null) {
                                baseCellStyle.position = "sticky";
                                baseCellStyle.left = stickyOffset;
                                if (!hasIssue) {
                                  baseCellStyle.background = "#fff";
                                }
                                const stickyRank = Math.max(stickyKeys.indexOf(col.key), 0);
                                baseCellStyle.zIndex = 3 + stickyRank;
                              }

                              if (col.key === ACCESS_COLUMN_KEY) {
                                return (
                                  <td
                                    key={`${col.key}:${rIdx}:${cIdx}`}
                                    className={commonClasses}
                                    style={baseCellStyle}
                                    title={hasIssue && issue?.messages ? issue.messages.join(" · ") : accessLabel}
                                    {...issueHandlers}
                                  >
                                    <button
                                      type="button"
                                      className="access-toggle-button"
                                      onClick={() => rowKey && handleToggleRowAccess(rowKey)}
                                      disabled={!rowKey}
                                      aria-pressed={isRowConfirmed}
                                      aria-label={accessLabel}
                                      title={accessLabel}
                                      style={accessButtonStyles}
                                    >
                                      {isRowConfirmed ? (
                                        <i className="fas fa-check" aria-hidden="true" style={{ opacity: "100%" }} />
                                      ) : (
                                        <i className="fas fa-check" aria-hidden="true" style={{ opacity: "0%" }} />
                                      )}
                                    </button>
                                  </td>
                                );
                              }

                              const val = row[col.key];
                              const isIcon = col.kind === "active";
                              const isPhoneColumn = phoneColumnKey && col.key === phoneColumnKey;
                              const phoneDisplay = isPhoneColumn
                                ? buildPhoneWithCountry(row[countryColumnKey], val).full || String(val ?? "")
                                : null;
                              const defaultText = !isIcon ? (val != null ? String(val) : "") : "";
                              const displayText = isPhoneColumn ? phoneDisplay : defaultText;
                              const cellTitle = hasIssue && issue?.messages
                                ? issue.messages.join(" · ")
                                : displayText;

                              if (col.kind === "photo") {
                                const status = val?.status;
                                const url = val?.url;

                                let content = null;
                                if (status === "ok" && url) {
                                  content = (
                                    <img
                                      src={url}
                                      alt={`${col.label} INE`}
                                      style={{
                                        maxHeight: 40,
                                        maxWidth: 90,
                                        objectFit: "cover",
                                        borderRadius: 4,
                                        display: "block",
                                        margin: "0 auto",
                                      }}
                                    />
                                  );
                                } else if (status === "error") {
                                  content = <span style={{ color: "#c00" }}>Error</span>;
                                } else {
                                  content = <span style={{ color: "#777" }}>Sin Foto</span>;
                                }

                                return (
                                  <td
                                    key={`${col.key}:${rIdx}:${cIdx}`}
                                    className={commonClasses}
                                    style={baseCellStyle}
                                    title={cellTitle}
                                    {...issueHandlers}
                                  >
                                    {content}
                                  </td>
                                );
                              }

                              return (
                                <td
                                  key={`${col.key}:${rIdx}:${cIdx}`}
                                  className={commonClasses}
                                  style={baseCellStyle}
                                  title={cellTitle}
                                  {...issueHandlers}
                                >
                                  {isIcon ? (
                                    isActiveTruthy(val) ? (
                                      <i className="fas fa-check-circle color-green" aria-label="Activo" />
                                    ) : (
                                      <i className="fas fa-times-circle color-gray" aria-label="Inactivo" />
                                    )
                                  ) : (
                                    displayText
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {(validationSummary.errors.length > 0 || validationSummary.warnings.length > 0) && (
              <div style={{ marginTop: 16, width: "100%" }}>
                {validationSummary.errors.length > 0 && (
                  <div
                    style={{
                      border: "1px solid #d14343",
                      background: "#fff5f5",
                      borderRadius: 6,
                      padding: "12px 16px",
                    }}
                  >
                    <p style={{ fontWeight: 600, margin: "0 0 8px 0" }}>
                      Corrige estos datos antes de crear ({validationSummary.errors.length})
                    </p>
                    <ul style={{ margin: 0, paddingLeft: 18, color: "#7a0000" }}>
                      {errorPreview.map((err, idx) => (
                        <li key={`error-${idx}`}>{err}</li>
                      ))}
                    </ul>
                    {extraErrors > 0 && (
                      <p style={{ margin: "8px 0 0 0", color: "#7a0000" }}>y {extraErrors} más...</p>
                    )}
                  </div>
                )}
                {validationSummary.warnings.length > 0 && (
                  <div
                    style={{
                      border: "1px solid #c77b00",
                      background: "#fff9ee",
                      borderRadius: 6,
                      padding: "12px 16px",
                    }}
                  >
                    <p style={{ fontWeight: 600, margin: "0 0 8px 0" }}>
                      Advertencias ({validationSummary.warnings.length})
                    </p>
                    <ul style={{ margin: 0, paddingLeft: 18, color: "#6d4200" }}>
                      {warningPreview.map((warn, idx) => (
                        <li key={`warning-${idx}`}>{warn}</li>
                      ))}
                    </ul>
                    {extraWarnings > 0 && (
                      <p style={{ margin: "8px 0 0 0", color: "#6d4200" }}>y {extraWarnings} más...</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : null}
      </div>
      {hoverIssue && (
        <div
          style={{
            position: "fixed",
            left: hoverIssue.x,
            top: hoverIssue.y - 8,
            transform: "translate(-50%, -100%)",
            background: hoverIssue.type === "warning" ? "#fff4d6" : "#ffe2e2",
            color: "#2c2c2c",
            border: `1px solid ${hoverIssue.type === "warning" ? "#d08a00" : "#d14343"}`,
            borderRadius: 6,
            padding: "8px 10px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            pointerEvents: "none",
            zIndex: 9999,
            maxWidth: 280,
            fontSize: 12,
            lineHeight: 1.35,
            textAlign: "center",
          }}
        >
          {hoverIssue.message}
        </div>
      )}
    </div>
  );
});

export default ProveedoresModal;
